// Chemin: C:\gestion-copro\src\types\evenement.types.ts
// Types pour le module Calendrier / Agenda
// Session 94 : Création initiale
// Collection Firestore : coproprietes/{coproprieteId}/evenements/{evenementId}

import { Timestamp } from 'firebase/firestore';

// ============================================
// TYPES D'ÉVÉNEMENTS
// ============================================

export type TypeEvenement =
  | 'ag'                  // Assemblée Générale
  | 'reunion_cs'          // Réunion du Conseil Syndical
  | 'rdv'                 // Rendez-vous (syndic, expert, huissier...)
  | 'echeance_garantie'   // GPA, biennale, décennale, DO
  | 'echeance_contrat'    // Fin contrat syndic, prestataire, assurance
  | 'echeance_juridique'  // Contestation AG, prescription, délai réponse
  | 'audience'            // Date d'audience tribunal
  | 'travaux'             // Travaux votés ou planifiés
  | 'appel_fonds'         // Appel de fonds trimestriel
  | 'autre';

export type RecurrenceEvenement = 'aucune' | 'annuelle' | 'trimestrielle' | 'mensuelle';

export type VisibiliteEvenement = 'tous' | 'cs_only' | 'coordinateur_only';

export type StatutAlerte = 'a_envoyer' | 'envoyee';

// ============================================
// SOUS-TYPE GARANTIE (pour echeance_garantie)
// ============================================

export type SousTypeGarantie = 'gpa' | 'biennale' | 'decennale' | 'dommages_ouvrage';

// ============================================
// INTERFACE ALERTE
// ============================================

export interface AlerteEvenement {
  joursAvant: number;       // J-30, J-7, J-1
  statut: StatutAlerte;
  dateEnvoi?: Timestamp;    // Date effective d'envoi
}

// ============================================
// INTERFACE PRINCIPALE
// ============================================

export interface Evenement {
  id?: string;
  titre: string;
  description?: string;
  dateDebut: Timestamp;
  dateFin?: Timestamp;       // Optionnel (ex: GPA = date unique d'expiration)
  type: TypeEvenement;
  sousTypeGarantie?: SousTypeGarantie; // Uniquement si type === 'echeance_garantie'
  recurrence: RecurrenceEvenement;
  visibilite: VisibiliteEvenement;
  alertes: AlerteEvenement[];
  
  // Liaisons optionnelles
  affaireId?: string;        // Lien vers une affaire
  interlocuteurId?: string;  // Lien vers un interlocuteur (ex: RDV expert)
  
  // Métadonnées
  createdBy: string;         // UID Firebase
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  coproprieteId: string;     // Pour requêtes directes si nécessaire
}

// ============================================
// FORMULAIRE (sans Timestamp, pour le state React)
// ============================================

export interface EvenementFormData {
  titre: string;
  description: string;
  dateDebut: string;         // Format ISO yyyy-MM-dd ou yyyy-MM-ddTHH:mm
  heureDebut: string;        // Format HH:mm (optionnel, séparé pour le formulaire)
  dateFin: string;
  heureFin: string;
  type: TypeEvenement;
  sousTypeGarantie: SousTypeGarantie | '';
  recurrence: RecurrenceEvenement;
  visibilite: VisibiliteEvenement;
  alerteJ30: boolean;
  alerteJ7: boolean;
  alerteJ1: boolean;
  affaireId: string;
  interlocuteurId: string;
}

// ============================================
// VALEURS PAR DÉFAUT FORMULAIRE
// ============================================

export const EVENEMENT_FORM_DEFAULTS: EvenementFormData = {
  titre: '',
  description: '',
  dateDebut: '',
  heureDebut: '',
  dateFin: '',
  heureFin: '',
  type: 'autre',
  sousTypeGarantie: '',
  recurrence: 'aucune',
  visibilite: 'cs_only',
  alerteJ30: false,
  alerteJ7: true,
  alerteJ1: true,
  affaireId: '',
  interlocuteurId: '',
};

// ============================================
// LABELS ET COULEURS
// ============================================

export const TYPE_EVENEMENT_LABELS: Record<TypeEvenement, string> = {
  ag: 'Assemblée Générale',
  reunion_cs: 'Réunion CS',
  rdv: 'Rendez-vous',
  echeance_garantie: 'Echéance garantie',
  echeance_contrat: 'Echéance contrat',
  echeance_juridique: 'Echéance juridique',
  audience: 'Audience',
  travaux: 'Travaux',
  appel_fonds: 'Appel de fonds',
  autre: 'Autre',
};

export const TYPE_EVENEMENT_COULEURS: Record<TypeEvenement, string> = {
  ag: 'bg-red-500',            // Rouge = critique, ne pas manquer
  reunion_cs: 'bg-blue-500',   // Bleu = interne CS
  rdv: 'bg-indigo-500',        // Indigo = professionnel
  echeance_garantie: 'bg-orange-500', // Orange = attention garanties
  echeance_contrat: 'bg-amber-500',   // Ambre = contractuel
  echeance_juridique: 'bg-red-600',   // Rouge foncé = juridique critique
  audience: 'bg-purple-500',   // Violet = judiciaire
  travaux: 'bg-teal-500',      // Teal = technique
  appel_fonds: 'bg-emerald-500', // Émeraude = financier
  autre: 'bg-gray-500',        // Gris = divers
};

export const TYPE_EVENEMENT_ICONES: Record<TypeEvenement, string> = {
  ag: 'Users',
  reunion_cs: 'UserCheck',
  rdv: 'CalendarClock',
  echeance_garantie: 'ShieldAlert',
  echeance_contrat: 'FileSignature',
  echeance_juridique: 'Scale',
  audience: 'Gavel',
  travaux: 'Wrench',
  appel_fonds: 'Banknote',
  autre: 'Calendar',
};

export const RECURRENCE_LABELS: Record<RecurrenceEvenement, string> = {
  aucune: 'Aucune',
  annuelle: 'Annuelle',
  trimestrielle: 'Trimestrielle',
  mensuelle: 'Mensuelle',
};

export const VISIBILITE_LABELS: Record<VisibiliteEvenement, string> = {
  tous: 'Tous (copropriétaires inclus)',
  cs_only: 'Conseil Syndical uniquement',
  coordinateur_only: 'Coordinateur uniquement',
};

export const SOUS_TYPE_GARANTIE_LABELS: Record<SousTypeGarantie, string> = {
  gpa: 'Garantie de Parfait Achèvement (1 an)',
  biennale: 'Garantie Biennale (2 ans)',
  decennale: 'Garantie Décennale (10 ans)',
  dommages_ouvrage: 'Assurance Dommages-Ouvrage (10 ans)',
};
